import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRouteError,
  useSearchParams,
} from 'react-router-dom';

import { getZitadelUserManager } from '../../services/authentication/zitadel-user-manager';
import { isDefined } from '../../utils/variables';
import { Button } from '../components/button/button';
import { FullPageBox } from '../components/full-page-box/full-page-box';
import { RedirectFullPageBox } from '../components/redirect/redirect-full-page-box';
import { Stack } from '../components/stack/stack';
import { useAuthState } from '../state/auth-state';
import { Center } from '../styles/center';
import { isAbsoluteUrl, sanitizeTarget } from './redirect-target';

/**
 *  The last exchange attempted, keyed by the authorization code it was for.
 *
 *  An authorization code is single-use: redeeming one twice fails with
 *  `Errors.AuthRequest.NoCode`, which is indistinguishable from a tampered callback. Module scope
 *  rather than component state, because the point is to survive the route being re-entered.
 */
let lastExchange: { code: string; accessToken: Promise<string> } | undefined;

/**
 *  Exchanges the authorization code Zitadel sent us back with.
 *
 *  A loader rather than an effect, and de-duplicated on the code itself. Both are needed, for
 *  different reasons:
 *
 *  - a loader runs once per navigation, outside React's render cycle, so StrictMode's double
 *    invocation of effects cannot spend the code twice;
 *  - a loader still re-runs whenever the router is rebuilt or the URL is revisited — a refresh or
 *    a back-navigation onto this route — so the exchange itself has to be idempotent.
 *
 *  Throws when `state` does not match the value stored when the login started, and when Zitadel
 *  returns an error response instead of a code. Both are handled by the route's `errorElement`.
 */
export const authCallbackLoader = async ({ request }: LoaderFunctionArgs) => {
  const code = new URL(request.url).searchParams.get('code');

  if (isDefined(code) && lastExchange?.code === code) {
    // Returns the in-flight or already-settled result, including a rejection: re-redeeming would
    // turn a recoverable failure into a permanent one.
    return lastExchange.accessToken;
  }

  const accessToken = getZitadelUserManager()
    .signinRedirectCallback()
    .then((user) => user.access_token);

  if (isDefined(code)) {
    lastExchange = { code, accessToken };
  }

  return accessToken;
};

/**
 *  Resets the de-duplication of {@link authCallbackLoader}. Only intended for tests.
 */
export const resetAuthCallbackExchange = () => {
  lastExchange = undefined;
};

/**
 *  OIDC error codes that mean the person could not prove who they are — as opposed to something
 *  going wrong in the exchange. A migrated user whose password predates Zitadel's policy lands
 *  here, and telling them to contact support would be the wrong advice: they need a new password.
 *
 *  Most of that experience is Zitadel's own hosted page and belongs to SK-7363. This is only the
 *  branch where Zitadel hands the failure back to us instead of keeping the user on its page.
 */
const CREDENTIAL_ERROR_CODES = new Set(['access_denied', 'login_required', 'interaction_required']);

type AuthCallbackFailure = 'credentials' | 'generic';

/**
 *  Reads the OIDC error out of whatever the loader threw, without assuming a shape.
 */
const describeError = (error: unknown) => {
  const candidate = error as { error?: unknown; error_description?: unknown; message?: unknown };

  return {
    code: typeof candidate?.error === 'string' ? candidate.error : undefined,
    description:
      typeof candidate?.error_description === 'string' ? candidate.error_description : undefined,
    message: typeof candidate?.message === 'string' ? candidate.message : undefined,
  };
};

const classifyFailure = (error: unknown): AuthCallbackFailure => {
  const { code } = describeError(error);

  return isDefined(code) && CREDENTIAL_ERROR_CODES.has(code) ? 'credentials' : 'generic';
};

/**
 *  Where Zitadel returns after a completed login (`ZITADEL_CALLBACK_PATH`).
 *
 *  Stores the access token through the same seam the Entra flow uses, so the axios interceptor and
 *  every existing consumer keep working. The refresh token stays in the oidc-client store, where
 *  the silent renew (SK-7315) will pick it up.
 */
export const AuthCallbackRoute = () => {
  const accessToken = useLoaderData<typeof authCallbackLoader>();
  const navigate = useNavigate();
  const { intendedRoute, setIntendedRoute, login } = useAuthState();

  // Captured on the first render: signing in consumes the intended route, and the destination must
  // not change underneath us once it has been read.
  const [target] = React.useState(() => sanitizeTarget(intendedRoute));

  React.useEffect(() => {
    void (async () => {
      await login(accessToken);
      setIntendedRoute(undefined);

      if (isAbsoluteUrl(target)) {
        window.location.assign(target);
        return;
      }

      // Replaces the callback entry, so the code and state are gone from the URL and from history.
      void navigate(target, { replace: true });
    })();
  }, [accessToken, login, setIntendedRoute, navigate, target]);

  return <RedirectFullPageBox />;
};

/**
 *  Rendered when the exchange fails. The user is left signed out, and is given a way forward
 *  rather than a dead end.
 */
export const AuthCallbackErrorRoute = () => {
  const { t } = useTranslation();
  const error = useRouteError();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const failure = classifyFailure(error);

  React.useEffect(() => {
    // The real reason goes to the console so a failure can be diagnosed without a network capture.
    // The authorization code and the token are deliberately not included.
    console.error('Zitadel sign-in could not be completed.', describeError(error));
  }, [error]);

  React.useEffect(() => {
    // A failed exchange must not leave the authorization code sitting in the URL or in history.
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return (
    <FullPageBox width="400px">
      <Stack direction="column" spacing={1}>
        <Center>
          <label>
            {failure === 'credentials'
              ? t('authCallback.passwordUpdateMessage', { ns: 'components' })
              : t('authCallback.errorMessage', { ns: 'components' })}
          </label>
        </Center>
        <Center>
          <Button
            color="primary"
            type="button"
            variant="contained"
            onClick={() => void navigate('/signin', { replace: true })}
          >
            {t('authCallback.backToSignIn', { ns: 'components' })}
          </Button>
        </Center>
      </Stack>
    </FullPageBox>
  );
};
